import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Upload, Cpu, Eye, Sparkles, FileOutput,
  FolderOpen, Shield, Users, Mail, Calendar, FileCheck,
  ChevronDown, ChevronRight as ChevRight, Zap, Hand, Bot,
  Layers, ScanLine, ClipboardCheck, Activity, Download, Route, ArrowUpRight, FileText
} from 'lucide-react';
import api from '@/api';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Journey phase → project tab (for in-project deep-linking)
const PHASE_TAB = {
  '1': 'intake', '2': 'assets', '3': 'assets', '4': 'assets', '5': 'submittals',
  '6': 'floorplan', '7': 'assets', '8': 'audit', '9': 'team', '10': 'team',
};

// Owner-edition wording (all phases, plain language)
const OWNER_TEXT = {
  '0': { summary: 'Your project gets set up in the system with the basics about your building.',
    bullets: ['Your team creates a workspace for the building.', 'Key facts are captured — name, address, team, and the completion date that starts your warranties.', 'Everything that follows is organized inside this one project.'] },
  '1': { summary: "All your building's documents are brought in and organized automatically.",
    bullets: ['Drawings, submittals, specs, and other documents are uploaded.', 'The system automatically files each document in the right place.', 'Large drawing sets are split into individual, easy-to-view sheets.'] },
  '2': { summary: "The system reads the drawings and builds a list of your building's equipment.",
    bullets: ['AI pulls out every piece of equipment shown on the drawings.', 'Each item is sorted by trade and given a standard classification code.', 'You end up with a clean, organized equipment list.'] },
  '3': { summary: 'Your team double-checks the equipment list for accuracy.',
    bullets: ['People review each item and fix any details.', 'Confirmed items are locked so they stay correct.', 'Locations are added so equipment can be shown on the floor plan.'] },
  '4': { summary: 'Any missing product information is chased down from the responsible contractors.',
    bullets: ['Items still missing details are flagged.', 'The right subcontractor is asked to supply the missing information.', 'Contractors can submit it through their own secure link.'] },
  '5': { summary: 'Approved products are matched against the equipment in your building.',
    bullets: ['Submittal documents are uploaded and read by AI.', 'Proposed products are matched to your equipment list.', 'Each match is approved so the record is accurate.'] },
  '6': { summary: 'Your equipment is placed on the floor plan so you can see where everything is.',
    bullets: ['A master floor plan is chosen for the building.', 'Equipment is pinned to its real location on the plan.', 'You can see, at a glance, where each asset lives.'] },
  '7': { summary: 'The system finds the warranty, maintenance, and manual information for your equipment.',
    bullets: ['AI gathers warranty terms for each piece of equipment.', 'It collects recommended maintenance tasks and schedules.', 'It links the official manufacturer manuals.'] },
  '8': { summary: 'A final quality check confirms your closeout package is complete.',
    bullets: ['Warranty dates and coverage are reviewed.', 'The package is scored for completeness.', 'A readiness report grades how ready the building is for handover.'] },
  '9': { summary: "The key people and service contacts you'll need are set up.",
    bullets: ['Your project team and stakeholders are listed.', 'Emergency and service contacts are added for your building.', 'These contacts appear in your owner handover portal.'] },
  '10': { summary: 'You receive a single, shareable handover package for your building.',
    bullets: ['A private web link gives you everything in one place.', 'Browse equipment, warranties, maintenance, floor plan, and contacts.', 'Download a CSV, the drawings, or a complete PDF/ZIP package anytime.'] },
  '11': { summary: 'Your handover package can power the day-to-day operation of the building.',
    bullets: ['Everything flows into live operations with no re-entry.', 'Track warranties, schedule maintenance, and manage work orders.', '(FMiQ-Operate is coming soon; the handover and demo request are live today.)'] },
};

// ─── Data ───────────────────────────────────────────────────
const JOURNEY = [
  { num: '0', title: 'Account & Project Setup', tag: 'Setup', color: '#64748B',
    summary: 'Create the project shell and capture the facts that drive everything downstream.',
    bullets: [
      { t: 'Sign in to FMiQ-One (PM / CM / GC account).' },
      { t: 'Land on the Closeout Dashboard — hub for all projects, asset counts, completion %, and alerts.' },
      { t: 'Click New Project and complete the setup form:', subs: [
        'Project name, number, and address',
        'General Contractor and Project Manager names',
        'Key dates — especially Substantial Completion / CO date (drives warranty start & expiration math)',
      ] },
      { t: 'Open the project to reach the 8-tab workspace with a 6-step Workflow Stepper up top.' },
    ] },
  { num: '1', title: 'Ingest', tag: 'Workflow Step — Intake', color: '#0EA5E9',
    summary: 'Bring every document into the system and let AI file it.',
    bullets: [
      { t: 'Upload documents into Intake. The app recognizes the type:', subs: [
        'Drawing Sets (as-built / construction drawings)', 'Submittals', 'Specifications', 'Other documents',
      ] },
      { t: 'AI classifies and files each document into the correct division/folder.' },
      { t: 'Process Drawings: drawing-set PDFs are split into individual sheets, rendered, and stored.' },
      { t: "The 'Ingest' stepper turns done once a drawing set / document is in place." },
    ] },
  { num: '2', title: 'Scan & Classify', tag: 'Workflow Step', color: '#2563EB',
    summary: 'The drawing engine (BUILD) reads the sheets and turns them into structured assets.',
    bullets: [
      { t: 'Extraction reads the PDF text layer first (vision only as a fallback for scanned sheets), then pulls equipment/assets from the drawings.' },
      { t: 'Shortcut: in ACTIVATE → Assets & Equipment → Build Register from Drawings runs this whole pipeline in one click and imports the result straight into the register.' },
      { t: 'Each asset is auto-classified:', subs: [
        'Inventory class — equipment vs design-chase (missing design info)',
        'CSI section / division and trade',
        'UniFormat II code (5-level, e.g., D3050.10.10) via the UniFormat AI agent',
      ] },
      { t: "The 'Scan & Classify' stepper tracks classified / total assets." },
    ] },
  { num: '3', title: 'Assets & Equipment Review', tag: 'Human-in-the-loop', color: '#6366F1',
    summary: 'A person confirms and corrects what the AI extracted.',
    bullets: [
      { t: 'Review extracted assets in the Assets & Equipment tab. For each asset you can:', subs: [
        'Edit attributes (make, model, location, specs)',
        'Verify an asset (verified assets are protected during re-extraction)',
        'Add/correct location so it can be placed on the floor plan later',
      ] },
      { t: 'Smart Re-extraction Merge: re-running extraction enriches missing attributes, preserves verified data, and logs differences for audit.' },
    ] },
  { num: '4', title: 'Design Chase', tag: 'Workflow Step', color: '#F59E0B',
    summary: 'Close the information gaps by chasing the responsible trades.',
    bullets: [
      { t: 'Assets flagged design-chase are missing required design/product info.' },
      { t: 'Chase the responsible subs/trades for the missing data:', subs: [
        'Chase Emails with escalation stages — Initial Request → Reminder → Escalation → Retainage Hold',
        'Smart Design Chase routing sends each request to the correct party',
      ] },
      { t: 'Trade Portal (public link per sub): subcontractors submit the requested attributes themselves, with an audit trail.' },
      { t: "The 'Design Chase' stepper turns done when no chase items remain." },
    ] },
  { num: '5', title: 'Submittals', tag: 'Workflow Step', color: '#06B6D4',
    summary: 'Reconcile approved products against the equipment of record.',
    bullets: [
      { t: 'Upload sub submittal documents.' },
      { t: 'AI extracts proposed products from each submittal.' },
      { t: 'The app auto-matches proposed products against your Equipment records.' },
      { t: 'You approve / flag / reject each match.' },
      { t: "The 'Submittals' stepper tracks the approval rate (done at ≥80%)." },
    ] },
  { num: '6', title: 'Floor Plans', tag: 'Spatial Context', color: '#14B8A6',
    summary: 'Give every asset a place on the plan and measure the building.',
    bullets: [
      { t: 'Set a master floor plan from your processed sheets.' },
      { t: 'Build spatial context:', subs: [
        'AI Room Detection — rooms, names, numbers with boundary polygons',
        'Pin assets — auto-plot by location keyword or drag-and-drop unmatched assets',
        'Multi-sheet overlays — layer discipline sheets with per-trade colors',
      ] },
      { t: 'Optional measurement tooling:', subs: [
        'Scale calibration, then Area / Linear / Count takeoff tools',
        'AI Takeoff (per-trade) and Batch AI Takeoff with accept/reject review',
        'Takeoff Comparison Report (AI vs manual) and Takeoff Heat Map',
      ] },
      { t: 'Handoff Checklist — a 9-point pre-export quality check.' },
    ] },
  { num: '7', title: 'Doc Finder', tag: 'Workflow Step', color: '#8B5CF6',
    summary: 'AI discovers warranty, O&M, and manual data for each piece of equipment.',
    bullets: [
      { t: 'Run the AI Doc Finder across equipment to locate, per asset:', subs: [
        'Warranty details (parts/labor years, coverage, registration URL, conditions)',
        'O&M maintenance tasks (task + frequency + category)',
        'OEM manual links (reference documents)',
      ] },
      { t: "The 'Doc Finder' stepper tracks equipment with warranty/O&M (done at ≥80%)." },
    ] },
  { num: '8', title: 'Warranties, Quality Gates & Audit', tag: 'Readiness', color: '#EC4899',
    summary: 'Confirm the package is complete and defensible before handover.',
    bullets: [
      { t: 'Warranties tab — review the binder and calendar; expirations compute from the substantial-completion date.' },
      { t: 'Quality Gates tab — verify readiness metrics (assets, submittals, documents, coverage %).' },
      { t: 'Audit tab — run the Handover Readiness Audit:', subs: [
        'AI-generated overall readiness grade (A–F) and %',
        'Per-trade scorecard with notes and a summary report',
      ] },
    ] },
  { num: '9', title: 'Team & Contacts', tag: 'Collaboration', color: '#0891B2',
    summary: 'Set up the people the owner will rely on after handover.',
    bullets: [
      { t: 'Manage project members and stakeholders.' },
      { t: 'Maintain Service Contacts (Emergency / Service / General) — these appear in the owner Handover Portal.' },
      { t: 'Set digest preferences and monitor demo-request leads captured from the portal.' },
    ] },
  { num: '10', title: 'Handover Package', tag: 'Workflow Step — Owner-facing', color: '#10B981',
    summary: 'Publish a shareable, no-login portal for the building owner.',
    bullets: [
      { t: 'Review before publish: Generate Handover and Email Handover Link (owner/admin only) first open a soft-gate review summary of open items — unverified assets, needs-review items, assumed quantities, missing make/model, unverified warranties. Acknowledge each section (logged as a timestamped sign-off in the Audit tab) or Generate anyway.' },
      { t: 'Generate a shareable Handover link (/handover/{token}) — a public portal for the owner / facilities team.' },
      { t: 'The owner portal contains:', subs: [
        'Overview — stats, project info, and a 90-day warranty-expiration alert banner',
        'Asset Cards — searchable, grouped by trade, expandable',
        'Floor Plan — master plan with hover markers for asset locations',
        'Warranty Binder — coverage, parts/labor years, expiration dates',
        'O&M Manual — separate tables: Preventive Maintenance Tasks and O&M Documents & Manuals',
        'Contacts, Approved Submittals, and Audit Summary',
      ] },
      { t: 'Downloads:', subs: [
        'Asset Schedule (CSV) for CMMS/CAFM import',
        'As-built Drawing Sets and Submittal files as direct downloads',
        'Full PDF Bundle (ZIP) — cover page, asset schedule, warranty binder, O&M index + CSV',
      ] },
      { t: "The 'Handover' stepper turns done once the package link is generated." },
    ] },
  { num: '11', title: 'Operate', tag: 'Live Operations', color: '#7C3AED',
    summary: 'Hand the closeout package straight into live building operations.',
    bullets: [
      { t: 'From the Handover Portal, the owner can Request an FMiQ-Operate demo (captures a lead, notifies the PM).' },
      { t: "FMiQ-Operate takes this exact package into live operations — warranty tracking, PM scheduling, work orders, lifecycle management: 'one click, zero re-entry.'" },
      { t: 'Note: FMiQ-Operate (and Space / Recover / Predict / Assess) is currently a Coming Soon destination; the handover hand-off and demo-capture are live, the operate workspace is a placeholder.' },
    ] },
];

// ─── Data ───────────────────────────────────────────────────

const LEGEND = [
  { color: 'bg-sky-500', border: 'border-sky-500/40', label: 'Ingest Point', desc: 'Data enters the system' },
  { color: 'bg-emerald-500', border: 'border-emerald-500/40', label: 'Fully Automated', desc: 'No human intervention' },
  { color: 'bg-amber-500', border: 'border-amber-500/40', label: 'AI-Assisted', desc: 'AI does work, human confirms' },
  { color: 'bg-rose-500', border: 'border-rose-500/40', label: 'Manual', desc: 'Human-driven step' },
  { color: 'bg-violet-500', border: 'border-violet-500/40', label: 'Real-Time', desc: 'SSE live streaming' },
];

const DRAWING_STEPS = [
  {
    id: 'di-ingest',
    title: 'Ingest Drawings',
    type: 'ingest',
    icon: Upload,
    ai: null,
    inputs: ['PDF', 'PNG', 'JPG', 'TIFF'],
    outputs: ['Sheets in Object Storage'],
    detail: 'Upload construction drawing files. Each file becomes a sheet. Multi-page PDFs are split into individual pages. Files are persisted in Emergent Object Storage.',
    manual: 'User selects and uploads files',
    route: '/upload',
  },
  {
    id: 'di-classify',
    title: 'Page Classification',
    type: 'ai',
    icon: ScanLine,
    ai: 'Gemini 3 Flash',
    inputs: ['Sheet images', 'Native PDF text'],
    outputs: ['Page type', 'Discipline', 'Confidence score'],
    detail: 'Each page is classified into a type: floor plan, room schedule, equipment schedule, MEP plan, lease plan, cover sheet, detail, elevation, section. Heuristic rules try first — Gemini Flash handles uncertain cases cheaply.',
    manual: null,
    route: null,
  },
  {
    id: 'di-extract',
    title: 'AI Data Extraction',
    type: 'ai',
    icon: Sparkles,
    ai: 'GPT-4o Vision',
    inputs: ['Classified page images (300 DPI)'],
    outputs: ['Rooms / Spaces', 'Equipment / Assets', 'Title blocks', 'Schedule rows'],
    detail: 'Based on page type, the right extractor runs: room schedules, equipment schedules, lease plan (quadrant-split), title blocks, and plan annotations. Each extraction is an AI Vision call with structured JSON output.',
    manual: null,
    route: null,
  },
  {
    id: 'di-cv',
    title: 'CV Polygon Detection',
    type: 'auto',
    icon: Cpu,
    ai: null,
    inputs: ['Sheet images'],
    outputs: ['Room boundary polygons', 'Bounding boxes', 'Pixel areas'],
    detail: 'OpenCV BPOLY wall-tracing algorithm detects room boundaries as closed polygons. Produces vertices, bounding boxes, centroids, and pixel-based area measurements. Runs in dense mode for maximum accuracy.',
    manual: null,
    route: null,
  },
  {
    id: 'di-review',
    title: 'Human Review',
    type: 'manual',
    icon: Eye,
    ai: null,
    inputs: ['Extracted spaces', 'Assets', 'Polygons'],
    outputs: ['Approved / Rejected entities', 'Edited fields'],
    detail: 'Interactive CAD editor overlays polygons on the floor plan. Users approve, reject, or edit each space and asset. Polygons can be drawn, moved, or deleted. Bidirectional linking between polygons and spaces.',
    manual: 'Approve/reject each entity, edit room numbers, draw polygons',
    route: '/jobs/:id/review',
  },
  {
    id: 'di-autolabel',
    title: 'AI Auto-Labeling',
    type: 'ai',
    icon: Bot,
    ai: 'GPT-4o Vision',
    inputs: ['Polygon crop images'],
    outputs: ['Room numbers', 'Room names', 'Space types'],
    detail: 'Crops each unlabeled polygon from the drawing and sends it to GPT-4o Vision in batches of 5. AI reads text annotations, room numbers, and architectural context to assign labels. Creates or updates linked space records.',
    manual: 'User initiates; results auto-apply',
    route: null,
  },
  {
    id: 'di-calibrate',
    title: 'Scale Calibration',
    type: 'manual',
    icon: Hand,
    ai: null,
    inputs: ['Known distance reference'],
    outputs: ['Pixels-per-foot ratio', 'Real-world areas (SF)'],
    detail: 'User draws a calibration line between two known points and enters the real-world distance. System calculates pixels-per-foot ratio and converts all polygon pixel areas to square footage (BOMA-compliant).',
    manual: 'User draws reference line and enters distance',
    route: null,
  },
  {
    id: 'di-export',
    title: 'Export',
    type: 'auto',
    icon: FileOutput,
    ai: null,
    inputs: ['Approved building model'],
    outputs: ['DXF (CAD)', 'PDF report', 'JSON model', 'ZIP package'],
    detail: 'Generates CAD-accurate DXF files with polylines, PDF summary reports, structured JSON building models, and complete digitization packages (ZIP). Client portal sharing with time-limited access codes.',
    manual: null,
    route: null,
  },
];

const CLOSEOUT_STEPS = [
  {
    id: 'co-create',
    title: 'Create Project Shell',
    type: 'manual',
    icon: FolderOpen,
    ai: null,
    inputs: ['Project name', 'Number', 'Address', 'Phase'],
    outputs: ['Project record', '5 project-level divisions'],
    detail: 'Creates a CBC SOP-compliant project shell. Auto-generates 5 project-level divisions (01 General, 02 Existing, 03 Concrete, 07 Thermal, 08 Openings) with 9 standard folders each (A through I).',
    manual: 'User fills project form',
    route: '/closeout/new',
  },
  {
    id: 'co-g703',
    title: 'G703 SOV Import',
    type: 'ingest',
    icon: Upload,
    ai: null,
    inputs: ['CSV file or manual entry'],
    outputs: ['Trade divisions', '9 folders per division'],
    detail: 'Import Schedule of Values via CSV upload, manual entry, or default trade templates. Each trade division gets auto-created with 9 SOP folders (A: O&M Manuals, B: Warranties, C: As-Builts, D: Attic Stock, E: Training, F: Commissioning, G: Spare Parts, H: Submittals, I: Misc).',
    manual: 'User uploads CSV or enters trades',
    route: null,
  },
  {
    id: 'co-wizard',
    title: 'Applicability Wizard',
    type: 'manual',
    icon: Shield,
    ai: null,
    inputs: ['Division list', 'Folder grid'],
    outputs: ['Required / Conditional / N/A per folder'],
    detail: 'Step-through wizard lets users configure which folders are Required, Conditional, or Not Applicable for each division. Bulk save applies to all divisions at once. Progress bar shows completion.',
    manual: 'User sets applicability for each folder across all divisions',
    route: null,
  },
  {
    id: 'co-docfiling',
    title: 'Document Filing',
    type: 'ai',
    icon: FileCheck,
    ai: 'GPT-4o Vision',
    inputs: ['Uploaded document (PDF/image)'],
    outputs: ['Suggested division + folder', 'CBC-named file record'],
    detail: 'Upload a document and GPT-4o Vision reads it to suggest the correct CSI division and SOP folder. AI provides confidence score and reasoning. Admin confirms or overrides the suggestion before filing.',
    manual: 'User confirms AI suggestion or overrides',
    route: null,
  },
  {
    id: 'co-warranty',
    title: 'Warranty Tracking',
    type: 'ai',
    icon: Calendar,
    ai: 'GPT-4o Vision',
    inputs: ['Warranty certificate (PDF/image)'],
    outputs: ['Structured warranty record', 'Start/end dates', 'Status alerts'],
    detail: 'Upload warranty certificates and GPT-4o extracts vendor, terms, start date, end date, and coverage details. Auto-computes status (Active / Expiring Soon / Expired). Visual calendar timeline with 30/60/90-day alert windows.',
    manual: 'User uploads certificate; reviews AI extraction',
    route: null,
  },
  {
    id: 'co-chase',
    title: 'Chase Email Drafts',
    type: 'ai',
    icon: Mail,
    ai: 'GPT-4o',
    inputs: ['Missing items', 'Stakeholder contacts', 'SOP cadence'],
    outputs: ['Draft emails per escalation stage'],
    detail: 'AI drafts professional chase emails per SOP escalation cadence: Day 7 (friendly reminder), Day 14 (formal request), Day 21 (escalation notice), and retainage hold warning. One-click copy to clipboard.',
    manual: 'User reviews drafts before sending externally',
    route: null,
  },
  {
    id: 'co-stakeholders',
    title: 'Stakeholder Registry',
    type: 'manual',
    icon: Users,
    ai: null,
    inputs: ['Company', 'Contact', 'Role', 'Division links'],
    outputs: ['Stakeholder records', 'Outstanding item counts'],
    detail: 'Track all project stakeholders (subcontractors, vendors, consultants). Auto-create from division vendor names. Links to divisions show outstanding document counts and completion percentages.',
    manual: 'User creates/edits stakeholders; auto-create from vendors',
    route: null,
  },
  {
    id: 'co-gates',
    title: 'Quality Gates',
    type: 'manual',
    icon: Shield,
    ai: null,
    inputs: ['Project completion percentage'],
    outputs: ['Gate status (Locked/Pending/Approved/Rejected)'],
    detail: 'Three-gate system with prerequisite enforcement. Gate 1 (Admin, 50%): basic documentation. Gate 2 (PM+CxA, 80%): substantial completion. Gate 3 (Owner, 95%): final acceptance. Each gate must be approved before the next unlocks.',
    manual: 'Reviewer approves/rejects each gate',
    route: null,
  },
  {
    id: 'co-gap',
    title: 'Gap Report',
    type: 'auto',
    icon: Activity,
    ai: null,
    inputs: ['Division matrix', 'Folder status'],
    outputs: ['Missing items list', 'Completeness percentages'],
    detail: 'Automated analysis scans all divisions and folders to identify gaps — required documents that are missing or incomplete. Generates a prioritized report showing what still needs to be collected for closeout.',
    manual: null,
    route: null,
  },
];

const REALTIME_LAYER = [
  {
    id: 'rt-pipeline',
    title: 'Pipeline Progress Stream',
    covers: ['di-classify', 'di-extract', 'di-cv'],
    detail: 'SSE streams page-by-page progress during extraction: queued → classified → extracting → done/error. Frontend shows LIVE connection indicator.',
  },
  {
    id: 'rt-labeling',
    title: 'Auto-Label Progress Stream',
    covers: ['di-autolabel'],
    detail: 'SSE streams per-polygon labeling progress with live count badge in the editor toolbar.',
  },
];

// ─── Components ─────────────────────────────────────────────

const typeStyles = {
  ingest: { bg: 'bg-sky-950/40', border: 'border-sky-500/30', dot: 'bg-sky-500', text: 'text-sky-400', label: 'INGEST' },
  auto: { bg: 'bg-emerald-950/40', border: 'border-emerald-500/30', dot: 'bg-emerald-500', text: 'text-emerald-400', label: 'AUTO' },
  ai: { bg: 'bg-amber-950/40', border: 'border-amber-500/30', dot: 'bg-amber-500', text: 'text-amber-400', label: 'AI-ASSISTED' },
  manual: { bg: 'bg-rose-950/40', border: 'border-rose-500/30', dot: 'bg-rose-500', text: 'text-rose-400', label: 'MANUAL' },
};

function StepNode({ step, expanded, onToggle }) {
  const s = typeStyles[step.type];
  const Icon = step.icon;

  return (
    <div
      className={`rounded-sm border ${s.border} ${s.bg} transition-all cursor-pointer hover:brightness-110`}
      onClick={onToggle}
      data-testid={`step-${step.id}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className={`w-8 h-8 rounded-sm ${s.bg} border ${s.border} flex items-center justify-center shrink-0`}>
          <Icon className={`w-4 h-4 ${s.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-heading font-bold text-xs uppercase tracking-wider text-foreground truncate">
              {step.title}
            </h3>
            <span className={`px-1.5 py-0.5 rounded text-[8px] font-mono uppercase tracking-wider ${s.text} border ${s.border}`}>
              {s.label}
            </span>
          </div>
          {step.ai && (
            <span className="text-[10px] font-mono text-amber-500/80 mt-0.5 block">
              {step.ai}
            </span>
          )}
        </div>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevRight className="w-4 h-4 text-muted-foreground shrink-0" />
        )}
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-border/50 space-y-3">
          <p className="text-xs text-muted-foreground leading-relaxed">{step.detail}</p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground block mb-1">Inputs</span>
              <div className="flex flex-wrap gap-1">
                {step.inputs.map((inp) => (
                  <span key={inp} className="px-1.5 py-0.5 bg-muted/60 border border-border/50 rounded text-[9px] font-mono text-muted-foreground">
                    {inp}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <span className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground block mb-1">Outputs</span>
              <div className="flex flex-wrap gap-1">
                {step.outputs.map((out) => (
                  <span key={out} className="px-1.5 py-0.5 bg-muted/60 border border-border/50 rounded text-[9px] font-mono text-muted-foreground">
                    {out}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {step.manual && (
            <div className="flex items-start gap-2 mt-1">
              <Hand className="w-3 h-3 text-rose-400 mt-0.5 shrink-0" />
              <span className="text-[10px] text-rose-400/80">{step.manual}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FlowArrow({ vertical }) {
  if (vertical) {
    return (
      <div className="flex justify-center py-1">
        <div className="w-px h-6 bg-gradient-to-b from-slate-700 to-slate-800 relative">
          <ArrowRight className="w-3 h-3 text-muted-foreground absolute -bottom-1.5 -left-1.5 rotate-90" />
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center px-1 shrink-0">
      <div className="w-6 h-px bg-slate-700" />
      <ArrowRight className="w-3 h-3 text-muted-foreground -ml-1" />
    </div>
  );
}

// ─── Summary Stats ──────────────────────────────────────────

function SummaryStats() {
  const allSteps = [...DRAWING_STEPS, ...CLOSEOUT_STEPS];
  const auto = allSteps.filter(s => s.type === 'auto').length;
  const ai = allSteps.filter(s => s.type === 'ai').length;
  const manual = allSteps.filter(s => s.type === 'manual').length;
  const ingest = allSteps.filter(s => s.type === 'ingest').length;
  const total = allSteps.length;

  const stats = [
    { label: 'Total Steps', value: total, color: 'text-foreground' },
    { label: 'Fully Automated', value: auto, color: 'text-emerald-400' },
    { label: 'AI-Assisted', value: ai, color: 'text-amber-400' },
    { label: 'Manual', value: manual, color: 'text-rose-400' },
    { label: 'Ingest Points', value: ingest, color: 'text-sky-400' },
    { label: 'AI Models Used', value: '2', color: 'text-violet-400', sub: 'GPT-4o + Gemini Flash' },
  ];

  return (
    <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-8" data-testid="process-map-stats">
      {stats.map((st) => (
        <div key={st.label} className="bg-card border border-border rounded-sm p-3 text-center">
          <div className={`font-mono text-2xl font-bold ${st.color}`}>{st.value}</div>
          <div className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground mt-1">{st.label}</div>
          {st.sub && <div className="text-[8px] font-mono text-muted-foreground mt-0.5">{st.sub}</div>}
        </div>
      ))}
    </div>
  );
}

// ─── Journey Card ───────────────────────────────────────────

function JourneyCard({ phase, isLast, edition, projectId }) {
  const owner = edition === 'owner';
  const ot = OWNER_TEXT[phase.num] || {};
  const summary = owner ? (ot.summary || phase.summary) : phase.summary;
  const tab = PHASE_TAB[phase.num];
  const deepLink = projectId && tab ? `/closeout/${projectId}?tab=${tab}` : null;

  return (
    <div className="relative pl-14" data-testid={`journey-phase-${phase.num}`}>
      {/* connector line */}
      {!isLast && (
        <div className="absolute left-[21px] top-11 bottom-0 w-px bg-border" />
      )}
      {/* number badge */}
      <div
        className="absolute left-0 top-1 w-11 h-11 rounded-md flex items-center justify-center shadow-sm"
        style={{ backgroundColor: phase.color }}
      >
        <span className="font-mono font-bold text-base text-white">{phase.num}</span>
      </div>

      <div className="bg-card border border-border rounded-sm p-4 mb-5">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-foreground">
            {phase.title}
          </h3>
          <span
            className="px-1.5 py-0.5 rounded text-[8px] font-mono uppercase tracking-wider"
            style={{ color: phase.color, backgroundColor: `${phase.color}1a`, border: `1px solid ${phase.color}40` }}
          >
            {phase.tag}
          </span>
          {deepLink && (
            <Link to={deepLink} data-testid={`journey-deeplink-${phase.num}`}
              className="ml-auto flex items-center gap-1 text-[10px] font-heading uppercase tracking-wider text-primary hover:underline">
              Open <ArrowUpRight className="w-3 h-3" />
            </Link>
          )}
        </div>
        <p className="text-xs italic text-muted-foreground leading-relaxed mb-3">{summary}</p>

        <ul className="space-y-1.5">
          {owner ? (
            (ot.bullets || []).map((line, i) => (
              <li key={i} className="flex items-start gap-2 text-[11px] text-foreground/90 leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-sm shrink-0" style={{ backgroundColor: phase.color }} />
                <span>{line}</span>
              </li>
            ))
          ) : (
            phase.bullets.map((b, i) => (
              <li key={i}>
                <div className="flex items-start gap-2 text-[11px] text-foreground/90 leading-relaxed">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-sm shrink-0" style={{ backgroundColor: phase.color }} />
                  <span>{b.t}</span>
                </div>
                {b.subs && (
                  <ul className="mt-1 ml-5 space-y-1">
                    {b.subs.map((sub, j) => (
                      <li key={j} className="flex items-start gap-2 text-[10px] text-muted-foreground leading-relaxed">
                        <span className="mt-1.5 h-px bg-muted-foreground/60 shrink-0" style={{ width: 8 }} />
                        <span>{sub}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────

export default function ProcessMapPage() {
  const [expandedStep, setExpandedStep] = useState(null);
  const [activeSection, setActiveSection] = useState('journey');
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project') || null;
  const [edition, setEdition] = useState('pm');
  const [projectName, setProjectName] = useState('');

  useEffect(() => {
    if (projectId) {
      api.get(`/closeout/projects/${projectId}`)
        .then(r => setProjectName(r.data?.project_name || r.data?.name || ''))
        .catch(() => {});
    }
  }, [projectId]);

  const toggleStep = (id) => setExpandedStep(prev => prev === id ? null : id);

  const showJourney = activeSection === 'all' || activeSection === 'journey';
  const showDrawing = activeSection === 'all' || activeSection === 'drawing';
  const showCloseout = activeSection === 'all' || activeSection === 'closeout';
  const guideUrl = `${BACKEND}/api/process-map/guide.pdf?edition=${edition}`;

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-7xl mx-auto" data-testid="process-map-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-2 gap-4 flex-wrap">
        <div>
          <Link to={projectId ? `/closeout/${projectId}` : '/dashboard'} className="text-xs text-muted-foreground hover:text-muted-foreground font-mono flex items-center gap-1 mb-2">
            <ArrowLeft className="w-3 h-3" /> {projectId ? 'Back to Project' : 'Back to Dashboard'}
          </Link>
          <h1 className="font-heading font-bold text-xl uppercase tracking-wider text-foreground">
            Process Map
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            The full user journey plus the under-the-hood ingestion & AI pipelines
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Edition toggle */}
          <div className="flex items-center gap-1 bg-muted/50 border border-border rounded-sm p-0.5" data-testid="edition-toggle">
            {[{ key: 'pm', label: 'PM Edition' }, { key: 'owner', label: 'Owner Edition' }].map(e => (
              <button key={e.key} onClick={() => setEdition(e.key)}
                data-testid={`edition-${e.key}`}
                className={`px-3 py-1.5 rounded-sm text-[10px] font-heading uppercase tracking-wider transition-all ${
                  edition === e.key ? 'bg-primary/15 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground/80 border border-transparent'
                }`}>
                {e.label}
              </button>
            ))}
          </div>
          <a
            href={guideUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="download-guide-pdf-btn"
            className="flex items-center gap-2 px-3 py-2 rounded-sm bg-primary text-primary-foreground text-[10px] font-heading uppercase tracking-wider hover:brightness-110 transition-all"
          >
            <Download className="w-3.5 h-3.5" /> Download Guide (PDF)
          </a>
          <a
            href={`${BACKEND}/api/process-map/training-manual.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="download-training-manual-btn"
            className="flex items-center gap-2 px-3 py-2 rounded-sm border border-primary/40 text-primary text-[10px] font-heading uppercase tracking-wider hover:bg-primary/10 transition-all"
          >
            <FileText className="w-3.5 h-3.5" /> Training Manual (PDF)
          </a>
          <div className="flex items-center gap-1 bg-muted/50 border border-border rounded-sm p-0.5" data-testid="section-filter">
            {[
              { key: 'journey', label: 'User Journey' },
              { key: 'all', label: 'All' },
              { key: 'drawing', label: 'Drawing Intel' },
              { key: 'closeout', label: 'Closeout SOP' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveSection(tab.key)}
                className={`px-3 py-1.5 rounded-sm text-[10px] font-heading uppercase tracking-wider transition-all ${
                  activeSection === tab.key
                    ? 'bg-primary/15 text-primary border border-primary/30'
                    : 'text-muted-foreground hover:text-foreground/80 border border-transparent'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {projectId && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-primary/10 border border-primary/20 rounded-sm" data-testid="project-context-banner">
          <Route className="w-3.5 h-3.5 text-primary" />
          <span className="text-[11px] text-muted-foreground">
            Linked to project <strong className="text-foreground">{projectName || projectId}</strong> — each phase can jump straight to its tab.
          </span>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-6 py-3 px-4 bg-card/60 border border-border/50 rounded-sm" data-testid="process-map-legend">
        <span className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Legend</span>
        {LEGEND.map((l) => (
          <div key={l.label} className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${l.color}`} />
            <span className="text-[10px] font-mono text-muted-foreground">{l.label}</span>
          </div>
        ))}
      </div>

      <SummaryStats />

      {/* How BUILD & ACTIVATE Work Together */}
      <section className="mb-10" data-testid="build-activate-explainer">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-8 h-8 rounded-sm bg-primary/15 border border-primary/30 flex items-center justify-center">
            <ArrowRight className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-sm uppercase tracking-wider text-foreground">
              How BUILD &amp; ACTIVATE Work Together
            </h2>
            <p className="text-[10px] text-muted-foreground">Two workspaces, one data spine — drawings in BUILD feed the closeout &amp; handover in ACTIVATE</p>
          </div>
        </div>

        {/* Two workspaces */}
        <div className="grid md:grid-cols-[1fr_auto_1fr] gap-4 items-stretch mb-5">
          <div className="bg-card border border-blue-500/30 rounded-sm p-4" data-testid="explainer-build">
            <div className="flex items-center gap-2 mb-2">
              <Layers className="w-4 h-4 text-blue-400" />
              <h3 className="font-heading font-bold text-xs uppercase tracking-wider text-blue-400">BUILD — Drawing Intelligence</h3>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed mb-2">
              Where drawings live. Upload a construction / as-built set as a <strong className="text-foreground/90">Job</strong>. The engine reads the
              PDF text layer first (vision only as a fallback for scanned sheets), splits it into sheets, classifies each page, and extracts
              schedules, equipment tags, make / model, rooms, and fixture locations.
            </p>
            <ul className="space-y-1">
              {['Review sheets, schedules & fixture pins', 'Text-layer-first extraction, vision fallback', 'Jobs are owned by whoever creates them'].map(t => (
                <li key={t} className="flex items-start gap-2 text-[10px] text-foreground/80">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-sm bg-blue-500 shrink-0" />{t}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex md:flex-col items-center justify-center gap-1 text-muted-foreground">
            <ArrowRight className="w-5 h-5 text-primary" />
            <span className="text-[8px] font-mono uppercase tracking-widest text-primary">feeds</span>
          </div>

          <div className="bg-card border border-emerald-500/30 rounded-sm p-4" data-testid="explainer-activate">
            <div className="flex items-center gap-2 mb-2">
              <ClipboardCheck className="w-4 h-4 text-emerald-400" />
              <h3 className="font-heading font-bold text-xs uppercase tracking-wider text-emerald-400">ACTIVATE — Closeout &amp; Handover</h3>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed mb-2">
              The project home and source of truth for closeout: the <strong className="text-foreground/90">equipment register</strong>, warranties,
              PM / O&amp;M program, floor plans, contacts, and the owner <strong className="text-foreground/90">handover package</strong>.
            </p>
            <ul className="space-y-1">
              {['Equipment register with grouped assets & locations', 'Warranties + PM/O&M auto-enriched by Doc Finder', 'Secure owner handover portal & emailed link'].map(t => (
                <li key={t} className="flex items-start gap-2 text-[10px] text-foreground/80">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-sm bg-emerald-500 shrink-0" />{t}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* How they talk */}
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="bg-primary/5 border border-primary/20 rounded-sm p-4" data-testid="explainer-activate-first">
            <div className="flex items-center gap-2 mb-1.5">
              <Zap className="w-3.5 h-3.5 text-primary" />
              <h4 className="font-heading font-bold text-[11px] uppercase tracking-wider text-primary">ACTIVATE-first (one click — recommended)</h4>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              In an ACTIVATE project, open <strong className="text-foreground/90">Assets &amp; Equipment → Build Register from Drawings</strong> and upload your
              PDF(s). Behind the scenes it creates a BUILD Job, runs extraction, groups duplicate fixtures into one asset with multiple locations,
              imports the register, enriches warranty + PM / O&amp;M data, and syncs warranties — four manual steps collapsed into one. Run
              <strong className="text-foreground/90"> one trade at a time</strong> (e.g. plumbing) or a broader set.
            </p>
          </div>
          <div className="bg-card border border-border rounded-sm p-4" data-testid="explainer-build-first">
            <div className="flex items-center gap-2 mb-1.5">
              <Layers className="w-3.5 h-3.5 text-blue-400" />
              <h4 className="font-heading font-bold text-[11px] uppercase tracking-wider text-blue-400">BUILD-first</h4>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Already processed a Job in BUILD? Its sheets, pins, and schedules stay in BUILD, and ACTIVATE register rows deep-link back to the
              exact sheet + highlighted pin via <strong className="text-foreground/90">View on drawing</strong>. (A picker to attach an existing BUILD
              Job to a project is on the roadmap.)
            </p>
          </div>
        </div>

        {/* Two audiences + review gate */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-card border border-border rounded-sm p-4" data-testid="explainer-audiences">
            <div className="flex items-center gap-2 mb-1.5">
              <Users className="w-3.5 h-3.5 text-cyan-400" />
              <h4 className="font-heading font-bold text-[11px] uppercase tracking-wider text-cyan-400">Two ways to use it</h4>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Teams who just want a <strong className="text-foreground/90">closeout / handover package</strong> can stay in the one-click
              drawings → register → warranties → handover lane. Teams who want <strong className="text-foreground/90">deeper project management</strong>
              {' '}use the full ACTIVATE workflow — intake, design chase, submittals, quality gates, and audit.
            </p>
          </div>
          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-300 dark:border-amber-800/40 rounded-sm p-4" data-testid="explainer-review-gate">
            <div className="flex items-center gap-2 mb-1.5">
              <Eye className="w-3.5 h-3.5 text-amber-500" />
              <h4 className="font-heading font-bold text-[11px] uppercase tracking-wider text-amber-600 dark:text-amber-400">Human review before publish</h4>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Build Register from Drawings fills the project but never auto-publishes. <strong className="text-foreground/90">Generate Handover</strong> or
              {' '}<strong className="text-foreground/90">Email Handover Link</strong> (owner / admin only) first opens a review summary of open items —
              unverified assets, items needing review, assumed quantities, missing make / model, unverified warranties. It's a soft gate: acknowledge
              each section (recorded as a timestamped sign-off in the <strong className="text-foreground/90">Audit tab</strong>) or Generate anyway.
            </p>
          </div>
        </div>
      </section>

      {/* User Journey — Project Start → Operate */}
      {showJourney && (
        <section className="mb-10" data-testid="journey-section">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-8 rounded-sm bg-primary/15 border border-primary/30 flex items-center justify-center">
              <Route className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-sm uppercase tracking-wider text-foreground">
                User Journey — Project Start to Operate
              </h2>
              <p className="text-[10px] text-muted-foreground">Setup → Ingest → Scan → Review → Chase → Submittals → Floor Plans → Doc Finder → Readiness → Team → Handover → Operate</p>
            </div>
          </div>

          <div className="bg-sky-50 dark:bg-sky-950/20 border border-sky-200 dark:border-sky-900/40 rounded-sm px-4 py-3 mb-6 flex items-center gap-3">
            <Download className="w-4 h-4 text-primary shrink-0" />
            <p className="text-[11px] text-muted-foreground">
              Prefer a hand-off document? <a href={guideUrl} target="_blank" rel="noopener noreferrer" className="text-primary font-medium hover:underline">Download the polished PDF guide</a> — perfect for onboarding your team and clients.
            </p>
          </div>

          <div>
            {JOURNEY.map((phase, i) => (
              <JourneyCard key={phase.num} phase={phase} isLast={i === JOURNEY.length - 1}
                edition={edition} projectId={projectId} />
            ))}
          </div>
        </section>
      )}

      {/* Drawing Intelligence Pipeline */}
      {showDrawing && (
        <section className="mb-10" data-testid="drawing-pipeline-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-sm bg-blue-100 dark:bg-blue-900/20 border border-orange-800/30 flex items-center justify-center">
              <Layers className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-sm uppercase tracking-wider text-foreground">
                Drawing Intelligence Pipeline
              </h2>
              <p className="text-[10px] text-muted-foreground">Ingest → Classify → Extract → Detect → Review → Label → Export</p>
            </div>
          </div>

          {/* Real-time overlay badge */}
          <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-violet-950/20 border border-violet-800/30 rounded-sm">
            <Zap className="w-3.5 h-3.5 text-violet-400" />
            <span className="text-[10px] font-heading uppercase tracking-wider text-violet-400">Real-Time SSE</span>
            <span className="text-[10px] text-muted-foreground ml-1">— Live progress streaming during Classification, Extraction, and Auto-Labeling</span>
          </div>

          <div className="space-y-0">
            {DRAWING_STEPS.map((step, i) => (
              <div key={step.id}>
                <StepNode
                  step={step}
                  expanded={expandedStep === step.id}
                  onToggle={() => toggleStep(step.id)}
                />
                {i < DRAWING_STEPS.length - 1 && <FlowArrow vertical />}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Construction Closeout Pipeline */}
      {showCloseout && (
        <section className="mb-10" data-testid="closeout-pipeline-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-sm bg-teal-600/15 border border-teal-800/30 flex items-center justify-center">
              <ClipboardCheck className="w-4 h-4 text-teal-400" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-sm uppercase tracking-wider text-foreground">
                Construction Closeout SOP
              </h2>
              <p className="text-[10px] text-muted-foreground">Create → Import → Configure → File → Track → Chase → Gate → Report</p>
            </div>
          </div>

          <div className="space-y-0">
            {CLOSEOUT_STEPS.map((step, i) => (
              <div key={step.id}>
                <StepNode
                  step={step}
                  expanded={expandedStep === step.id}
                  onToggle={() => toggleStep(step.id)}
                />
                {i < CLOSEOUT_STEPS.length - 1 && <FlowArrow vertical />}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* AI Model Summary */}
      <section className="mb-10" data-testid="ai-models-section">
        <h2 className="font-heading font-bold text-sm uppercase tracking-wider text-foreground mb-4">
          AI Models & Integrations
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-sm bg-amber-600/15 border border-amber-800/30 flex items-center justify-center">
                <Sparkles className="w-3 h-3 text-amber-400" />
              </div>
              <h3 className="font-heading font-bold text-xs uppercase tracking-wider text-amber-400">GPT-4o Vision</h3>
            </div>
            <ul className="space-y-1.5">
              {[
                'Room schedule extraction',
                'Equipment schedule extraction',
                'Lease plan quadrant extraction',
                'Title block reading',
                'Polygon auto-labeling (crops)',
                'Document classification (closeout)',
                'Warranty certificate extraction',
                'Chase email generation',
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-[10px] text-muted-foreground">
                  <div className="w-1 h-1 rounded-full bg-amber-500 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-3 pt-3 border-t border-border/50">
              <span className="text-[9px] font-mono text-muted-foreground">Via Emergent LLM Key (emergentintegrations)</span>
            </div>
          </div>

          <div className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-sm bg-blue-600/15 border border-blue-800/30 flex items-center justify-center">
                <Cpu className="w-3 h-3 text-blue-400" />
              </div>
              <h3 className="font-heading font-bold text-xs uppercase tracking-wider text-blue-400">Gemini 3 Flash</h3>
            </div>
            <ul className="space-y-1.5">
              {[
                'Page type classification (cheap pre-filter)',
                'Discipline detection',
                'Confidence scoring for uncertain pages',
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-[10px] text-muted-foreground">
                  <div className="w-1 h-1 rounded-full bg-blue-500 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-3 pt-3 border-t border-border/50">
              <span className="text-[9px] font-mono text-muted-foreground">Via Emergent LLM Key (emergentintegrations)</span>
            </div>

            <div className="flex items-center gap-2 mt-4 mb-3">
              <div className="w-6 h-6 rounded-sm bg-emerald-600/15 border border-emerald-800/30 flex items-center justify-center">
                <Cpu className="w-3 h-3 text-emerald-400" />
              </div>
              <h3 className="font-heading font-bold text-xs uppercase tracking-wider text-emerald-400">OpenCV</h3>
            </div>
            <ul className="space-y-1.5">
              {[
                'BPOLY wall-tracing polygon detection',
                'Room boundary extraction',
                'Image enhancement & preprocessing',
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-[10px] text-muted-foreground">
                  <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-3 pt-3 border-t border-border/50">
              <span className="text-[9px] font-mono text-muted-foreground">Local processing (no API key required)</span>
            </div>
          </div>
        </div>
      </section>

      {/* What's Still Manual */}
      <section className="mb-6" data-testid="manual-summary-section">
        <h2 className="font-heading font-bold text-sm uppercase tracking-wider text-foreground mb-4">
          What's Still Manual
        </h2>
        <div className="bg-card border border-rose-800/20 rounded-sm p-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-[10px] font-heading uppercase tracking-widest text-rose-400 mb-2">Drawing Intelligence</h3>
              <ul className="space-y-1.5">
                {[
                  { step: 'File Upload', note: 'Build → New Job — select drawing files to ingest' },
                  { step: 'Human Review', note: 'Job Detail table approves/rejects spaces · Drawing editor edits rooms & assets' },
                  { step: 'Polygon Drawing', note: 'Drawing editor → Draw tool — trace room boundaries' },
                ].map((item) => (
                  <li key={item.step} className="flex items-start gap-2 text-[10px]">
                    <Hand className="w-3 h-3 text-rose-400 mt-0.5 shrink-0" />
                    <div>
                      <span className="text-foreground/80 font-medium">{item.step}</span>
                      <span className="text-muted-foreground ml-1">— {item.note}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-[10px] font-heading uppercase tracking-widest text-rose-400 mb-2">Construction Closeout</h3>
              <ul className="space-y-1.5">
                {[
                  { step: 'Project Creation', note: 'Activate → New Project — fill the details form' },
                  { step: 'G703 Import', note: 'Project header → Import G703 — upload CSV or enter trades' },
                  { step: 'Applicability Wizard', note: 'Project header → Wizard — set Required/Conditional/N/A per folder' },
                  { step: 'Scale Calibration', note: 'Floor Plans tab → Calibrate — draw a reference line for pixel-to-feet' },
                  { step: 'Document Filing', note: '⋯ menu → Upload Document — override the AI division/folder before uploading' },
                  { step: 'Warranty Review', note: 'Warranties tab — upload certificates and review AI-extracted info' },
                  { step: 'Chase Email Send', note: '⋯ menu → Chase Emails — copy the AI draft and send externally' },
                  { step: 'Stakeholder Management', note: 'Team → Stakeholders — create/edit contacts' },
                  { step: 'Quality Gate Approval', note: 'Quality Gates tab — sign off each gate (unlocks at its completion threshold)' },
                ].map((item) => (
                  <li key={item.step} className="flex items-start gap-2 text-[10px]">
                    <Hand className="w-3 h-3 text-rose-400 mt-0.5 shrink-0" />
                    <div>
                      <span className="text-foreground/80 font-medium">{item.step}</span>
                      <span className="text-muted-foreground ml-1">— {item.note}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
